import type { PointerEvent as ReactPointerEvent } from 'react'
import type { Anchor } from '@/types'
import type { Point, TableBounds } from '@/utils/geometry'
import {
  enforceOrthogonalPropagation,
  clampAnchorWithSideSwitch,
  anchorFromPoint,
} from '@/utils/paths'
import { useDiagramStore, useHistoryStore, useSelectionStore, useViewportStore } from '@/stores'
import { useTabViewStore } from '@/stores/tabViewStore'

function commitDiagramHistory(label: string) {
  const { tables, rels, groups, defaults, projectName, dialect } = useDiagramStore.getState()
  const hiddenTableIds = useTabViewStore.getState().hiddenTableIds
  useHistoryStore.getState().commitHistory({ tables, rels, groups, defaults, projectName, dialect, hiddenTableIds }, label)
}

export function useHandleDrag() {
  return (
    event: ReactPointerEvent<SVGCircleElement>,
    relationId: string,
    waypointIndex: number,         // index in the interior waypoints array (0-based)
    currentWaypoints: Point[],     // current interior bend points
    computedFromAnchor: Anchor,    // anchor used at drag start for source table
    computedToAnchor: Anchor,      // anchor used at drag start for target table
    fromAnchorPoint: Point,        // canvas-space position of source anchor at drag start
    toAnchorPoint: Point,          // canvas-space position of target anchor at drag start
    fromTableRect: TableBounds,    // source table bounds
    toTableRect: TableBounds,      // target table bounds
  ) => {
    event.preventDefault()
    event.stopPropagation()
    useSelectionStore.getState().selectRelationship(relationId)

    const startClientX = event.clientX
    const startClientY = event.clientY

    // Full path at drag start: [fromAnchor, ...waypoints, toAnchor]
    const originalAll: Point[] = [fromAnchorPoint, ...currentWaypoints, toAnchorPoint]

    // Index of the dragged point inside the full path (offset +1 for the leading anchor)
    const movedIdx = waypointIndex + 1

    let changed = false
    let isFinished = false

    const onPointerMove = (moveEvent: PointerEvent) => {
      const sc = useViewportStore.getState().sc
      const dx = (moveEvent.clientX - startClientX) / sc
      const dy = (moveEvent.clientY - startClientY) / sc

      // Copy all points and apply the drag
      const pts = originalAll.map((p) => ({ ...p }))
      pts[movedIdx].x += dx
      pts[movedIdx].y += dy

      // Propagate to keep every segment at 90°
      enforceOrthogonalPropagation(pts, originalAll, movedIdx)

      // Clamp anchors with automatic side-switching: if the bend moved to the other side of the
      // table, the anchor switches to the matching edge and the stub is realigned.
      const actualFromSide = clampAnchorWithSideSwitch(pts, 0, fromTableRect)
      const actualToSide   = clampAnchorWithSideSwitch(pts, pts.length - 1, toTableRect)

      // Derive updated Anchor descriptors from the clamped positions
      const newFromAnchor = anchorFromPoint(pts[0], actualFromSide, fromTableRect)
      const newToAnchor   = anchorFromPoint(pts[pts.length - 1], actualToSide, toTableRect)

      // Interior waypoints only
      const newWaypoints = pts.slice(1, -1)

      useDiagramStore.setState((state) => ({
        rels: state.rels.map((item) => {
          if (item.id !== relationId) return item
          return {
            ...item,
            waypoints:   newWaypoints,
            isManual:    true,
            fromAnchor:  newFromAnchor,
            toAnchor:    newToAnchor,
          }
        }),
      }))
      changed = true
    }

    const cleanup = () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('keydown', onKeyDown)
      isFinished = true
    }

    const onPointerUp = () => {
      cleanup()
      if (changed) commitDiagramHistory(`move-waypoint:${relationId}:${waypointIndex}`)
    }

    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== 'Escape' || isFinished) return
      cleanup()
      // Restore original state on Escape
      useDiagramStore.setState((state) => ({
        rels: state.rels.map((item) => {
          if (item.id !== relationId) return item
          return {
            ...item,
            waypoints:  currentWaypoints,
            fromAnchor: computedFromAnchor,
            toAnchor:   computedToAnchor,
          }
        }),
      }))
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp, { once: true })
    window.addEventListener('keydown', onKeyDown)

    event.currentTarget.setPointerCapture?.(event.pointerId)
  }
}
